import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { adminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Star, LogOut, Loader2, Save, FileText, Sparkles, Shield, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPrompts() {
  const { user, loading: authLoading, logout } = useAuth('admin_user');
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [changes, setChanges] = useState({});

  useEffect(() => {
    if (user) loadPrompts();
  }, [user]);

  const loadPrompts = async () => {
    try {
      const res = await adminApi.getPrompts();
      setPrompts(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, field, value) => {
    setChanges(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const handleSave = async (key) => {
    if (!changes[key]) return;
    
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await adminApi.updatePrompt(key, changes[key]);
      toast.success('Prompt gespeichert!');
      setChanges(prev => {
        const newChanges = { ...prev };
        delete newChanges[key];
        return newChanges;
      });
      loadPrompts();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleToggleEnabled = async (key, currentValue) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await adminApi.updatePrompt(key, { enabled: !currentValue });
      toast.success(currentValue ? 'Prompt deaktiviert' : 'Prompt aktiviert');
      loadPrompts();
    } catch (err) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const getPromptIcon = (key) => {
    switch (key) {
      case 'positive_review_suggestion': return <Sparkles className="w-5 h-5" />;
      case 'risk_analysis': return <Shield className="w-5 h-5" />;
      case 'rewrite_safe_neutral': return <RefreshCw className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const getPromptTitle = (key) => {
    switch (key) {
      case 'positive_review_suggestion': return 'Positive Review Vorschlag';
      case 'risk_analysis': return 'Risiko-Analyse';
      case 'rewrite_safe_neutral': return 'Neutrales Umschreiben';
      default: return key;
    }
  };

  const getPromptDescription = (key) => {
    switch (key) {
      case 'positive_review_suggestion':
        return 'Generiert Textvorschläge für positive Google-Bewertungen basierend auf Kategorien.';
      case 'risk_analysis':
        return 'Analysiert Bewertungstexte auf rechtliche Risiken, Emotionen und Sachlichkeit.';
      case 'rewrite_safe_neutral':
        return 'Formuliert kritische Texte neutral und rechtssicher um.';
      default:
        return '';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-primary text-primary-foreground border-b">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5" />
              </div>
              <span className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Immoverita Admin</span>
            </Link>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/admin" className="text-sm opacity-80 hover:opacity-100">Übersicht</Link>
            <Link to="/admin/tenants" className="text-sm opacity-80 hover:opacity-100">Tenants</Link>
            <Link to="/admin/reviews" className="text-sm opacity-80 hover:opacity-100">Bewertungen</Link>
            <Link to="/admin/prompts" className="text-sm font-medium">Prompts</Link>
            <Link to="/admin/coupons" className="text-sm opacity-80 hover:opacity-100">Gutscheine</Link>
            <Link to="/admin/meta-scores" className="text-sm opacity-80 hover:opacity-100">MetaScores</Link>
          </nav>
          <Button variant="ghost" size="icon" onClick={logout} className="text-primary-foreground hover:bg-white/20">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="container-main py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold">Prompt Konfiguration</h1>
          <p className="text-muted-foreground">
            Bearbeiten Sie die KI-Prompts für die verschiedenen Funktionen
          </p>
        </div>

        <div className="space-y-6">
          {prompts.map((prompt) => (
            <Card key={prompt.key} className="card-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      {getPromptIcon(prompt.key)}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{getPromptTitle(prompt.key)}</CardTitle>
                      <CardDescription>{getPromptDescription(prompt.key)}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={prompt.enabled ? 'default' : 'secondary'}>
                      {prompt.enabled ? 'Aktiv' : 'Deaktiviert'}
                    </Badge>
                    <Switch
                      checked={prompt.enabled}
                      onCheckedChange={() => handleToggleEnabled(prompt.key, prompt.enabled)}
                      disabled={saving[prompt.key]}
                      data-testid={`prompt-${prompt.key}-toggle`}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="system">
                    <AccordionTrigger>System Prompt</AccordionTrigger>
                    <AccordionContent>
                      <Textarea
                        value={changes[prompt.key]?.system_prompt ?? prompt.system_prompt}
                        onChange={(e) => handleChange(prompt.key, 'system_prompt', e.target.value)}
                        rows={12}
                        className="font-mono text-sm"
                        data-testid={`prompt-${prompt.key}-system-textarea`}
                      />
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="user">
                    <AccordionTrigger>User Template</AccordionTrigger>
                    <AccordionContent>
                      <Textarea
                        value={changes[prompt.key]?.user_prompt_template ?? prompt.user_prompt_template}
                        onChange={(e) => handleChange(prompt.key, 'user_prompt_template', e.target.value)}
                        rows={8}
                        className="font-mono text-sm"
                        data-testid={`prompt-${prompt.key}-user-textarea`}
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Verfügbare Variablen: {'{tenant_name}'}, {'{categories}'}, {'{notes}'}, {'{free_text}'}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {changes[prompt.key] && (
                  <div className="flex justify-end mt-4 pt-4 border-t">
                    <Button
                      onClick={() => handleSave(prompt.key)}
                      disabled={saving[prompt.key]}
                      data-testid={`prompt-${prompt.key}-save-button`}
                    >
                      {saving[prompt.key] ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Speichern
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
